/**
 * ConfirmPH Enhanced Vehicle Safety Enforcement, Phase D: the public
 * ConfirmPH vehicle QR/plate identity check.
 *
 * Route: /vehicle-check/:token
 * Public, no auth required. This is what a generic camera app opens when it
 * scans a vehicle's printed QR sticker, and it's also reachable by typing
 * the code shown on that sticker. It answers ONE question only: "is this
 * the exact ConfirmPH vehicle the code claims, and is it currently held?"
 * It is identity verification, never a mechanical safety certification -
 * see the explicit copy below.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, CheckCircle2, AlertTriangle, ShieldAlert, Car, Search,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export default function VehicleCheckPublic() {
  const { token: routeToken } = useParams();
  const [manualToken, setManualToken] = useState("");
  const [state, setState] = useState({ loading: !!routeToken, result: null, error: null });

  const check = async (t) => {
    if (!t || !t.trim()) return;
    setState({ loading: true, result: null, error: null });
    try {
      const r = await axios.get(`${API}/public/vehicle-check/${encodeURIComponent(t.trim())}`);
      setState({ loading: false, result: r.data, error: null });
    } catch (err) {
      setState({ loading: false, result: null, error: err?.response?.status || "error" });
    }
  };

  useEffect(() => {
    if (routeToken) check(routeToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeToken]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-display text-lg font-bold tracking-tight" data-testid="confirmph-logo">
            ConfirmPH
          </Link>
          <span className="text-[11px] text-slate-400 uppercase tracking-[0.18em]">Vehicle code check</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-7" data-testid="vehicle-check-card">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
            <Car className="w-3.5 h-3.5" /> ConfirmPH vehicle identity
          </div>
          <h1 className="font-display text-2xl font-semibold mt-2">Check a ConfirmPH vehicle code</h1>
          <p className="text-sm text-slate-600 mt-1">
            This confirms which ConfirmPH vehicle a code belongs to. It does not confirm
            mechanical safety, government certification or roadworthiness, and by itself it does
            not confirm the vehicle's legal readiness, insurance coverage, driver eligibility, or
            that the rental is ready for release.
          </p>

          {!routeToken && (
            <form className="mt-4 flex gap-2" onSubmit={(e) => { e.preventDefault(); check(manualToken); }}>
              <input value={manualToken} onChange={(e) => setManualToken(e.target.value)}
                     placeholder="Enter the code printed on the vehicle"
                     data-testid="vehicle-check-input"
                     className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              <button type="submit" data-testid="vehicle-check-submit"
                      className="px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm inline-flex items-center gap-1.5">
                <Search className="w-4 h-4" /> Check
              </button>
            </form>
          )}

          {state.loading && (
            <div className="flex items-center justify-center text-slate-500 py-10" data-testid="vehicle-check-loading">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Checking…
            </div>
          )}

          {!state.loading && state.error && (
            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="vehicle-check-invalid">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-slate-500 mt-0.5" />
                <div>
                  <div className="font-semibold text-slate-800">This ConfirmPH vehicle code is not valid.</div>
                  <p className="text-sm text-slate-600 mt-1">
                    It may have been rotated, mistyped, or never issued. If you're at a pickup,
                    ask staff to confirm the vehicle another way (plate number or visual check).
                  </p>
                </div>
              </div>
            </div>
          )}

          {!state.loading && state.result && (
            <div className="mt-5 space-y-3">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5" data-testid="vehicle-check-valid">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5" />
                  <div>
                    <div className="font-semibold text-emerald-900">
                      {state.result.vehicle?.make} {state.result.vehicle?.model}
                      {state.result.vehicle?.color ? ` · ${state.result.vehicle.color}` : ""}
                    </div>
                    {state.result.vehicle?.plate_number && (
                      <div className="text-sm text-emerald-900/80 font-mono mt-0.5">
                        {state.result.vehicle.plate_number}
                      </div>
                    )}
                    <p className="text-xs text-emerald-900/70 mt-1">
                      {state.result.company_name ? `ConfirmPH vehicle registered to ${state.result.company_name}.` : "ConfirmPH-registered vehicle."}
                    </p>
                  </div>
                </div>
              </div>

              {state.result.safety_hold_active && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-5" data-testid="vehicle-check-held">
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="w-5 h-5 text-red-700 mt-0.5" />
                    <div>
                      <div className="font-semibold text-red-900">
                        This vehicle cannot currently be handed over through ConfirmPH
                      </div>
                      <p className="text-sm text-red-900/80 mt-1">
                        Please contact the rental business for another vehicle, or ask staff about
                        a replacement.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-slate-400">
                This check confirms vehicle identity only - it means "this is the ConfirmPH
                vehicle this code belongs to." It is not a certification of mechanical safety, and
                it is not a verified-vehicle badge: legal readiness, insurance, driver eligibility
                and final release readiness are separate checks.{" "}
                <Link to="/enhanced-safety" className="underline">Learn about Enhanced Safety Enforcement</Link>.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
